import React from 'react';
import { useWalletBalance } from '../../hooks/useWalletBalance';
import './BalanceDisplay.css';

/**
 * Safe Balance Display Component
 * Minimal version with extensive error handling
 */
const SafeBalanceDisplay = ({ 
  walletId = null, 
  tokenMint = null, 
  compact = false,
  onClick = null,
  className = ''
}) => {
  const {
    balance,
    isLoading,
    error,
    refreshBalance
  } = useWalletBalance(walletId);

  const handleRefresh = async () => {
    try {
      await refreshBalance();
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    }
  };

  const getSafeBalance = () => {
    try {
      if (!balance) return { value: '0', symbol: 'SOL' };

      // For SOL or no specific token
      if (!tokenMint || tokenMint === 'So11111111111111111111111111111111111111112') {
        let solValue = 0;
        
        if (balance.sol) {
          if (typeof balance.sol === 'number') {
            solValue = balance.sol;
          } else if (typeof balance.sol === 'object' && balance.sol.balance) {
            solValue = typeof balance.sol.balance === 'number' ? balance.sol.balance : parseFloat(balance.sol.balance) || 0;
          } else {
            solValue = parseFloat(balance.sol) || 0;
          }
        }

        return {
          value: solValue.toFixed(6),
          symbol: 'SOL'
        };
      }

      // For specific tokens
      let tokenValue = 0;
      let tokenSymbol = 'TOKEN';

      if (balance.tokens) {
        if (Array.isArray(balance.tokens)) {
          const token = balance.tokens.find(t => t.mint === tokenMint);
          if (token) {
            tokenValue = typeof token.balance === 'number' ? token.balance : parseFloat(token.balance) || 0;
            tokenSymbol = token.symbol || 'TOKEN';
          }
        } else if (typeof balance.tokens === 'object') {
          const tokenData = balance.tokens[tokenMint];
          if (tokenData) {
            tokenValue = typeof tokenData.balance === 'number' ? tokenData.balance : parseFloat(tokenData.balance) || 0;
            tokenSymbol = tokenData.symbol || 'TOKEN';
          }
        }
      }

      return {
        value: tokenValue.toFixed(6),
        symbol: tokenSymbol
      };
    } catch (error) {
      console.error('Error getting safe balance:', error);
      return { value: '0', symbol: 'SOL' };
    }
  };

  const displayBalance = getSafeBalance();

  if (compact) {
    return (
      <div 
        className={`balance-display compact ${className}`}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        {isLoading ? (
          <span className="balance-loading">Loading...</span>
        ) : error ? (
          <span className="balance-error">Error</span>
        ) : (
          <span className="balance-value">
            {displayBalance.value} {displayBalance.symbol}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`balance-display ${className}`}>
      <div className="balance-header">
        <span className="balance-label">
          {tokenMint ? 'Token Balance' : 'Wallet Balance'}
        </span>
        <button 
          className={`refresh-button ${isLoading ? 'loading' : ''}`}
          onClick={handleRefresh}
          disabled={isLoading}
          title="Refresh balance"
        >
          🔄
        </button>
      </div>

      <div className="balance-content">
        {isLoading ? (
          <div className="balance-loading">
            <span>Loading balance...</span>
          </div>
        ) : error ? (
          <div className="balance-error">
            <span>⚠️ Failed to load balance</span>
            <button className="retry-button" onClick={handleRefresh}>
              Retry
            </button>
          </div>
        ) : (
          <div 
            className={`balance-main ${onClick ? 'clickable' : ''}`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : 'default' }}
          >
            <span className="balance-value">
              {displayBalance.value}
            </span>
            <span className="balance-symbol">
              {displayBalance.symbol}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafeBalanceDisplay;